import {
  Button,
  Checkbox,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Ship } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { MarketBoat } from '../lib/api-client';
import { marketDataApi } from '../lib/api-client';
import { formatLengthFt } from '../lib/format';
import { queryKeys } from '../lib/query-keys';

interface MarketBoatReviewTableProps {
  orgPid: string;
  browseParams: Record<string, string | number | undefined>;
  excludedPids: string[];
  onExcludedChange: (pids: string[]) => void;
  enabled: boolean;
}

type CleanParams = Record<string, string | number>;

const PAGE_SIZE = 25;

function cleanBrowseParams(params: Record<string, string | number | undefined>): CleanParams {
  const result: CleanParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      result[key] = value;
    }
  }
  return result;
}

export function MarketBoatReviewTable({
  orgPid,
  browseParams,
  excludedPids,
  onExcludedChange,
  enabled,
}: MarketBoatReviewTableProps) {
  const [page, setPage] = useState(1);
  const cleanParams = cleanBrowseParams(browseParams);

  // Reset to page 1 when filters change
  const paramsKey = JSON.stringify(cleanParams);
  useEffect(() => {
    setPage(1);
  }, [paramsKey]);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.market.browse(orgPid, { ...cleanParams, page, limit: PAGE_SIZE }),
    queryFn: () => marketDataApi.browse({ ...cleanParams, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    enabled,
  });

  const boats: MarketBoat[] = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const excludedCount = excludedPids.length;

  const visiblePids = boats.map((b) => b.id);
  const visibleExcluded = visiblePids.filter((pid) => excludedPids.includes(pid));
  const allVisibleExcluded =
    visiblePids.length > 0 && visibleExcluded.length === visiblePids.length;
  const someVisibleExcluded = visibleExcluded.length > 0 && !allVisibleExcluded;

  // Header checkbox: checked = all visible are included (none excluded)
  // indeterminate = some excluded
  // unchecked = all excluded
  const headerChecked = visiblePids.length > 0 && visibleExcluded.length === 0;
  const headerIndeterminate = someVisibleExcluded;

  function toggleAll() {
    if (allVisibleExcluded) {
      // All are excluded → include all (remove all visible from excluded)
      onExcludedChange(excludedPids.filter((pid) => !visiblePids.includes(pid)));
    } else {
      // Some or none are excluded → exclude all visible
      const newExcluded = new Set([...excludedPids, ...visiblePids]);
      onExcludedChange([...newExcluded]);
    }
  }

  function toggleBoat(pid: string) {
    if (excludedPids.includes(pid)) {
      onExcludedChange(excludedPids.filter((p) => p !== pid));
    } else {
      onExcludedChange([...excludedPids, pid]);
    }
  }

  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-12 text-center">
        <Ship className="size-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          Select at least one region to preview matching boats
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-8 w-28" />
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableHead key={i}>
                    <Skeleton className="h-4 w-16" />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (boats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-12 text-center">
        <Ship className="size-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No boats match your filters</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{total} boats</span> match your filters
          {excludedCount > 0 && (
            <span className="ml-1 text-destructive">({excludedCount} excluded)</span>
          )}
        </p>
        <Button variant="outline" size="sm" onClick={toggleAll} className="h-8 text-xs">
          {allVisibleExcluded ? 'Select All' : 'Deselect All'}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={headerIndeterminate ? 'indeterminate' : headerChecked}
                  onCheckedChange={toggleAll}
                  aria-label="Toggle all visible boats"
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Length (ft)</TableHead>
              <TableHead>Cabins</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Charter Co.</TableHead>
              <TableHead>Home Base</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {boats.map((boat) => {
              const isExcluded = excludedPids.includes(boat.id);
              return (
                <TableRow key={boat.id} className={isExcluded ? 'opacity-50' : undefined}>
                  <TableCell>
                    <Checkbox
                      checked={!isExcluded}
                      onCheckedChange={() => toggleBoat(boat.id)}
                      aria-label={`Toggle ${boat.name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{boat.name}</TableCell>
                  <TableCell className="text-muted-foreground">{boat.model ?? '—'}</TableCell>
                  <TableCell>{formatLengthFt(boat.lengthM)}</TableCell>
                  <TableCell>{boat.cabins ?? '—'}</TableCell>
                  <TableCell>{boat.year ?? '—'}</TableCell>
                  <TableCell>{boat.charterName ?? '—'}</TableCell>
                  <TableCell>{boat.homeBase ?? '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
